import { useCallback, useEffect, useState } from "react";

import UserNotificationService from "src/Services/API/usernotification.service";
import {
  connectUserNotificationSocket,
  disconnectUserNotificationSocket,
} from "src/Services/socket/userNotificationSocket";

// Slow fallback only. The socket is what makes the badge update live; this
// catches the cases it can't cover — a dropped connection, or rows read from
// the technician app while this tab sat idle.
const FALLBACK_POLL_MS = 60000;

// In-tab signal for read-state changes. The gateway only emits when rows are
// created, so marking rows read on the notifications page has no socket event
// to ride on and the badge would sit stale until the next poll.
const LOCAL_REFRESH_EVENT = "user-notification-refresh";

export const requestUnreadCountRefresh = () => window.dispatchEvent(new Event(LOCAL_REFRESH_EVENT));

/**
 * Unread count behind the header bell. Refreshes on three signals: the gateway's
 * per-user event, the tab becoming visible again, and a slow fallback poll.
 *
 * `setUnreadCount` is exposed so the drawer can push its optimistic count
 * (marking a row read updates the badge before the server answers).
 */
const useNotificationUnreadCount = () => {
  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const res = await UserNotificationService.getUnreadCount();
      setUnreadCount(res?.data?.count ?? 0);
    } catch {
      // A failed count must never break the header.
    }
  }, []);

  useEffect(() => {
    refresh();

    const socket = connectUserNotificationSocket();

    // The room is joined per connection, so re-emit on every (re)connect —
    // a dropped socket that comes back would otherwise be in no room at all.
    const subscribe = () => socket.emit("user-notifications:subscribe");
    // The payload carries no content by design; it only says "re-read".
    const handleChanged = () => refresh();

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      connectUserNotificationSocket();
      subscribe();
      refresh();
      // Balance the extra connect above so the ref count stays honest.
      disconnectUserNotificationSocket();
    };

    socket.on("connect", subscribe);
    socket.on("user-notification-changed", handleChanged);
    if (socket.connected) subscribe();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener(LOCAL_REFRESH_EVENT, handleChanged);
    const handle = setInterval(refresh, FALLBACK_POLL_MS);

    return () => {
      clearInterval(handle);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener(LOCAL_REFRESH_EVENT, handleChanged);
      socket.off("connect", subscribe);
      socket.off("user-notification-changed", handleChanged);
      socket.emit("user-notifications:unsubscribe");
      disconnectUserNotificationSocket();
    };
  }, [refresh]);

  return { unreadCount, setUnreadCount, refresh };
};

export default useNotificationUnreadCount;
